import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationType } from '@app/shared/interfaces';
import { CurrencyCodePipe } from '@app/shared/pipes/currency-code/currency-code.pipe';
import { PosthogService } from '@app/shared/services/posthog/posthog.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { filter, map } from 'rxjs/operators';
import { Transaction } from '@app/modules/businesses/models/statements.models';
import { StatementsFacade } from '@app/modules/businesses/services/statements.facade';
import { MyBusinessRoutes } from '@app/modules/my-business/interfaces/constants';

@Component({
  standalone: false,
  selector: 'sbnb-transactions-list',
  templateUrl: './transactions-list.component.html',
  styleUrls: ['./transactions-list.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  providers: [CurrencyCodePipe],
})
export class TransactionsListComponent {
  displayedColumns = ['date', 'category', 'description', 'property', 'attachment', 'total', 'actions'];
  detailColumns = [
    'detailDate',
    'detailCategory',
    'detailDescription',
    'detailProperty',
    'detailTotal',
    'detailActions',
  ];

  public state$ = this.statementsFacade.transactionsState$;
  public transactionsImportEnabled$ = this.posthog.transactionsImportEnabled$;

  paginatedData$ = this.state$.pipe(
    filter((state) => !!state.data),
    map((state) => state.data)
  );

  pagination$ = this.state$.pipe(
    filter((state) => !!state.meta),
    map((state) => ({
      currentPage: state.meta.current_page,
      totalPages: state.meta.last_page,
      pageSize: state.meta.per_page,
      totalItems: state.meta.total,
    }))
  );

  public buttons = [
    {
      id: 'add',
      label: '+ Add Transaction',
    },
  ];

  public transactionRoute = MyBusinessRoutes.Transactions;

  constructor(
    private statementsFacade: StatementsFacade,
    private notificationService: ToastNotificationsService,
    private router: Router,
    private currencyCodePipe: CurrencyCodePipe,
    private posthog: PosthogService
  ) {
    this.statementsFacade.getTransactions();
  }

  whenDetailRow(index: number, row: Transaction) {
    return row.lines.length > 1;
  }

  onPageChange(page: number) {
    this.statementsFacade.getTransactions(page);
  }

  delete(transaction: Transaction) {
    if (!transaction.computed) {
      transaction.computed = {};
    }

    transaction.computed.deleting = true;

    this.statementsFacade.deleteTransaction(transaction.id).subscribe({
      error: (error) => {
        transaction.computed.deleting = false;
        this.notificationService.open(
          error?.message ?? 'An error occurred while deleting the transaction.',
          'OK',
          NotificationType.Error
        );
      },
      complete: () => {
        this.notificationService.open('Transaction deleted', 'OK', NotificationType.Success);
      },
    });
  }

  public onAddTransaction(buttonId: string) {
    this.router.navigate([this.transactionRoute, 'add']);
  }

  public openImportModal() {
    this.router.navigate([this.transactionRoute, 'import']);
  }

  public recurringTotalTooltip(transaction: Transaction) {
    if (transaction.lines.length > 1 || transaction.lines[0].quantity <= 1) {
      return null;
    }
    const { quantity, amount, markup = 0 } = transaction.lines[0];
    const nightlyTotal = amount + markup;

    return `${quantity} nights at ${this.currencyCodePipe.transform(transaction.currency)}${nightlyTotal}/night`;
  }
}
